<template>
  <a-dialog v-model="show" scrollable max-width="85vw" persistent>
    <v-card>
      <v-banner color="primaryLight1" dark sticky single-line tile>
        <v-toolbar color="primaryLight1" dark>
          <span>
            {{
              isSeriesDetailsTitle
                ? $t("agenda.hSeriesDetails")
                : $t("agenda.hMeetingDetails")
            }}
          </span>
          <v-progress-linear
            :active="loadingData"
            :indeterminate="loadingData"
            absolute
            bottom
            color="appMenu"
          ></v-progress-linear>
          <v-spacer></v-spacer>
          <CloseIcon @click="close()" :disabled="isDataSaving" />
        </v-toolbar>
      </v-banner>
      <v-card-text id="dialog-card-text" style="height: 350px">
        <v-container style="padding: 12px 0px 0px !important">
          <v-form ref="refMeetingForm" v-model="valid" lazy-validation>
            <v-row>
              <v-col cols="12" sm="12" class="pt-0 pb-0"> </v-col>
              <v-col cols="12" sm="12" class="pt-0 pb-0">
                <a-text-field
                  class="textField"
                  v-model="meetingObject.meetingName"
                  :label="$t('agenda.lblMeetingTitle')"
                  :requiredMessage="$t('agenda.ruleMeetingNameReq')"
                  required
                  dense
                  filled
                  @input="onMeetingNameChanged"
                ></a-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="8" lg="8" class="pt-0 pb-0">
                <date-time-range-picker
                  :startDate.sync="meetingObject.scheduletStartTimeInUTC"
                  :endDate.sync="meetingObject.scheduledEndTimeInUTC"
                  :enabled="!isDateRangePickerDisabled"
                ></date-time-range-picker>
              </v-col>
              <v-col cols="12" sm="12" md="4" lg="4" class="pt-0 pb-0">
                <div id="wrapper">
                  <a-text-field
                    class="locationField mb-1"
                    v-model.trim="meetingObject.location"
                    :label="$t('agenda.lblMeetingLocation')"
                    :requiredMessage="$t('agenda.ruleLocationReq')"
                    required
                    dense
                    filled
                  ></a-text-field>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6" class="pt-0 pb-0">
                <v-tooltip top :disabled="isTooltipDisabled" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on">
                      <v-select
                        v-bind="attrs"
                        class="selectField"
                        v-model="meetingType"
                        :items="meetingTypesList"
                        :label="`${$t('agenda.lblMeetingType')} *`"
                        :rules="[(v) => !!v || $t('agenda.ruleMeetingTypeReq')]"
                        required
                        dense
                        filled
                        :menu-props="{ bottom: true, offsetY: true }"
                        item-text="label"
                        item-value="id"
                        return-object
                        :disabled="isMeetingTypeDisabled"
                        @focus="focusSelect"
                        ref="refMeetingType"
                        id="meetingType"
                        @mouseover.native="isTooltipDisabled = false"
                      >
                        <template v-slot:item="{ item }">
                          <span>{{ item.label }}</span>
                          <v-spacer></v-spacer>
                        </template>
                      </v-select>
                    </div>
                  </template>
                  <span v-html="$t('agenda.tipMeetingTypeField')"></span>
                </v-tooltip>
              </v-col>
              <v-col cols="12" sm="6" class="pt-0 pb-0">
                <v-tooltip top :disabled="isTooltipDisabled" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on">
                      <v-select
                        class="selectField"
                        v-bind="attrs"
                        v-model="meetingObject.resolutionType"
                        :items="resolutionTypesList"
                        item-text="label"
                        :label="`${$t('agenda.lblResType')} *`"
                        dense
                        filled
                        :menu-props="{ bottom: true, offsetY: true }"
                        :rules="[(v) => !!v || $t('agenda.ruleResTypeReq')]"
                        required
                        @focus="focusSelect"
                        ref="refResType"
                        id="resType"
                        @mouseover.native="isTooltipDisabled = false"
                      ></v-select>
                    </div>
                  </template>
                  <span>{{ $t("agenda.tipResTypeField") }} </span>
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <meeting-participants
                  :meetingAtteendeelist="meetingObject.attendees"
                  @onSelection="onParticipantChanged"
                  mode="create"
                  :label="`${$t('agenda.lblAttendees')}`"
                ></meeting-participants>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <div id="wrapper">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-on="on"
                        v-bind="attrs"
                        class="textField"
                        v-model.trim="meetingObject.meeting_link"
                        :label="$t('agenda.lblMeetingLink')"
                        :rules="meetingLinkRule"
                        hide-details="auto"
                        dense
                        filled
                        :maxlength="350"
                      ></v-text-field>
                    </template>
                    <span>{{ $t("agenda.tipMeetingLink") }} </span>
                  </v-tooltip>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="12" lg="12">
                <v-text-field
                  class="textField"
                  v-model.trim="meetingObject.meetingTheme"
                  :label="$t('agenda.lblMeetingTheme')"
                  dense
                  filled
                  :maxlength="350"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <div>
                  {{ $t("agenda.lblDescription") }}
                  <div v-if="showTextEditor">
                    <a-rich-text-editor
                      v-model="meetingObject.description"
                      @descriptionChange="descriptionChanges"
                      :tools="richtexteditorTools"
                      :isImageAllowed="true"
                    ></a-rich-text-editor>
                    <div v-if="isDescriptionWithImage">
                      <span style="color: var(--v-error-base) !important">{{
                        $t("imageFormatAllowed")
                      }}</span>
                    </div>
                  </div>
                  <v-skeleton-loader
                    v-else
                    v-bind="attrs"
                    type="list-item-three-line"
                  ></v-skeleton-loader>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-row no-gutters>
          <meeting-recurrence-settings
            v-if="isSeriesEdit"
            :startDate="startDateTime"
            :endDate="meetingObject.scheduledEndTimeInUTC"
            :recurrenceFormData.sync="recurrenceData"
            @reset="onResetRecurrence"
            :isNewMeeting="!meetingObject.id"
            :hasPastMeetings="meetingObject.has_past_meetings"
            @updateStartEndTime="onUpdateStartEndTime"
          >
          </meeting-recurrence-settings>
        </v-row>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-on="on"
              v-bind="attrs"
              color="primaryDark"
              class="mr-2"
              :disabled="isDataSaving || !isDirtyData || isDescriptionWithImage"
              @click.stop="saveMeeting"
            >
              {{ $t("btnSave") }}
            </a-btn>
          </template>
          <span> {{ $t("btnSave") }} </span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn v-on="on" v-bind="attrs" color="secondary" @click="close">
              {{ $t("btnClose") }}
            </a-btn>
          </template>
          <span> {{ $t("btnClose") }} </span>
        </v-tooltip>
      </v-card-actions>
      <message-sheet
        :showMessage="displayMessage"
        :message="message"
        :messageType="messageType"
        @close="displayMessage = false"
      ></message-sheet>
      <!-- <ConfirmDialog ref="confirm" /> -->
      <confirm-dialog ref="confirm" />
      <email-send-confirmation
        :emailData="emailData"
        :notificationMessage="mailNotificationMessage"
        @send="onEmailSend"
        @cancel="onEmailDialogCancel"
      ></email-send-confirmation>
      <meeting-email-notification
        v-if="showEmailDialog"
        v-model="showEmailDialog"
        :meetingObject="emailMeetingObject"
        @send="onEmailSend"
        :isMeetingCreated="isNewMeetingCreated"
      ></meeting-email-notification>
      <prevent-confirmation
        :prevent="isPrevent"
        @save="saveMeeting"
        @discard="preventClose(true)"
        @cancel="preventClose(false)"
      >
      </prevent-confirmation>
      <schedule-event-error-dialog
        v-model="isScheduleError"
        @discard="onDiscardMeeting"
      >
      </schedule-event-error-dialog>
    </v-card>
  </a-dialog>
</template>
<script>
import Vue from "vue";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import {
  RichTextEditorPlugin,
  Toolbar,
  Link,
  Count,
  HtmlEditor,
  QuickToolbar,
  Table,
} from "@syncfusion/ej2-vue-richtexteditor";
import { DateTimePickerPlugin } from "@syncfusion/ej2-vue-calendars";
import MeetingParticipants from "./MeetingParticipants.vue";
import MessageSheet from "../MessageSheet.vue";
import { createUUID, isURL } from "../../utils/util";
import _ from "lodash";
import DateTimeRangePicker from "../DateTimeRangePicker.vue";
import { Internationalization } from "@syncfusion/ej2-base";
import ConfirmDialog from "../../components/ConfirmDialog.vue";
import EmailSendConfirmation from "./EmailSendConfirmation.vue";
import PreventConfirmation from "../PreventConfirmation.vue";
import ADialog from "../../wrapper-component/ADialog.vue";
import ABtn from "../../wrapper-component/ABtn.vue";
import MeetingRecurrenceSettings from "@/components/agenda/MeetingRecurrenceSettings.vue";
import ScheduleEventErrorDialog from "./recurrence/ScheduleEventErrorDialog.vue";
import MeetingEmailNotification from "./MeetingEmailNotification.vue";

Vue.use(RichTextEditorPlugin);
Vue.use(DateTimePickerPlugin);

export default {
  name: "Meeting",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    selectedMeeting: Object,
    isSeriesEdit: {
      type: Boolean,
      default: true,
    },
    isCopy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isNewMeetingCreated: true,
      showEmailDialog: false,
      emailMeetingObject: null,
      isDescriptionWithImage: false,
      attrs: {
        class: "mb-6",
        boilerplate: true,
        elevation: 2,
      },
      isTooltipDisabled: false,
      showTextEditor: false,
      originalMeetingData: {},
      isPrevent: false,
      emailData: {},
      mailNotificationMessage: "",
      intl: new Internationalization(),
      isDataSaving: false,
      displayMessage: false,
      messageType: "",
      message: "",
      loadingData: false,
      startDate: new Date(),
      endDate: new Date(),
      meetingObject: this.selectedMeeting,
      calmenu: false,
      valid: true,
      menu1: false,
      menu2: false,
      isDirty: false,
      requiredRules: [(v) => !!v || "This field is mandatory"],
      meetingLinkRule: [
        (value) => this.isURL(value) || this.$t("agenda.validUrl"),
      ],
      showCharCount: true,
      dateFormat: "dd-MMM-yyyy hh:mm a",
      quickToolbarSettings: {
        table: [
          "TableHeader",
          "TableRows",
          "TableColumns",
          "TableCell",
          "-",
          "BackgroundColor",
          "TableRemove",
          "TableCellVerticalAlign",
          "Styles",
        ],
      },
      richtexteditorTools: ["Image"],
      maxLength: 3000,
    };
  },
  created() {
    //Code modified by Anil dated 15th Feb 2022
    /*  this.$refs.refMeetingForm.resetValidation();
    this.$refs.refMeetingForm.reset();  */
  },
  mounted() {
    this.isTooltipDisabled = false;
    if (this.selectedMeeting) {
      if (!this.selectedMeeting.id && !_.isEmpty(this.$refs)) {
        this.$refs.refMeetingForm.resetValidation();
        this.$refs.refMeetingForm.reset();
      }
      if (!this.selectedMeeting.description_meta) {
        this.selectedMeeting.description_meta = [];
      }
      this.selectedMeeting.description = this.bindMeetingDecriptionImages();
      if (this.isSeriesEdit && this.recurrenceData.recurrence_title) {
        this.selectedMeeting.meetingName = this.recurrenceData.recurrence_title;
      }
      this.originalMeetingData = _.cloneDeep(this.selectedMeeting);
      this.meetingObject = _.cloneDeep(this.selectedMeeting);
      if (this.isCopy) {
        this.meetingObject.meetingName = `${this.lblCopy} - ${this.meetingObject.meetingName}`;
      }
      this.isDataSaving = false;
    }
    const thisInstance = this;
    setTimeout(() => {
      thisInstance.showTextEditor = true;
    }, 200);
    this.scrolling();
  },
  components: {
    MeetingParticipants,
    MessageSheet,
    DateTimeRangePicker,
    ConfirmDialog,
    EmailSendConfirmation,
    PreventConfirmation,
    ADialog,
    ABtn,
    MeetingRecurrenceSettings,
    ScheduleEventErrorDialog,
    MeetingEmailNotification,
  },
  provide: {
    richtexteditor: [Toolbar, Link, Count, HtmlEditor, QuickToolbar, Table],
  },
  watch: {
    selectedMeeting() {
      this.isDataSaving = false;
      if (!this.selectedMeeting.id && !_.isEmpty(this.$refs)) {
        this.$refs.refMeetingForm.resetValidation();
        this.$refs.refMeetingForm.reset();
      }
    },
    "recurrenceData.recurrenceStartDate": {
      immediate: true,
      async handler(newValue, oldValue) {
        if (
          newValue &&
          oldValue &&
          JSON.stringify(new Date(newValue)) !==
            JSON.stringify(new Date(oldValue))
        ) {
          const hh = new Date(
            this.meetingObject.scheduletStartTimeInUTC
          ).getHours();
          const mm = new Date(
            this.meetingObject.scheduletStartTimeInUTC
          ).getMinutes();

          let newDate = new Date(newValue);

          newDate.setHours(hh);
          newDate.setMinutes(mm);

          this.meetingObject.scheduletStartTimeInUTC = new Date(
            newDate
          ).toISOString();
          // this.meetingObject.scheduledEndTimeInUTC = this.AddMinutesToDate(
          //   new Date(this.meetingObject.scheduletStartTimeInUTC),
          //   120
          // ).toISOString();
        }
      },
    },
    updateDescription(value) {
      this.checkDescriptionWithImage(value);
    },
    showEmailDialog(value) {
      if (!value) {
        this.onEmailDialogCancel();
      }
    },
  },
  computed: {
    ...mapState({
      userList: (state) => state.people.userList,
      isScheduleMeetingError: (state) => state.isScheduleMeetingError,
    }),
    ...gettersMap(),
    LoaderText() {
      return this.$t("SaveLoader");
    },
    isDirtyData() {
      const extractAttendees = (meeting) =>
        meeting.attendees
          ?.map((item) => {
            return {
              id: item.id,
              email: item.email,
              emailList: item.emailList ? item.emailList : [],
            };
          })
          .sort((a, b) => a.email.localeCompare(b.email)) || [];

      const originalMeetingAttendees = extractAttendees(
        this.originalMeetingData
      );
      let originalObject = {
        ...this.originalMeetingData,
        description: this.originalMeetingData.description,
      };
      const meetingObjectAttendees = extractAttendees(this.meetingObject);
      const originalMeetingData = {
        ...originalObject,
        attendees: originalMeetingAttendees,
      };

      let meetingObject = {
        ...this.meetingObject,
        attendees: meetingObjectAttendees,
      };
      return (
        JSON.stringify(originalMeetingData) !== JSON.stringify(meetingObject)
      );
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value || false);
      },
    },
    isScheduleError: {
      get() {
        return this.isScheduleMeetingError;
      },
      set(value) {
        if (!value) {
          this.setScheduleMeetingError(false);
        }
      },
    },
    participantList: {
      get() {
        return this.meetingObject.attendees;
      },
      set(val) {
        this.meetingObject.attendees = val;
      },
    },
    meetingType: {
      get() {
        return this.meetingObject.meetingType;
      },
      set(val) {
        this.meetingObject.meetingType = val;
      },
    },
    recurrenceData: {
      get() {
        return this.meetingObject.recurrenceData;
      },
      set(val) {
        this.meetingObject.recurrenceData = val;
      },
    },
    startDateTime: {
      get() {
        return this.meetingObject.scheduletStartTimeInUTC
          ? new Date(this.meetingObject.scheduletStartTimeInUTC)
          : new Date();
      },
      set(val) {
        this.meetingObject.scheduletStartTimeInUTC = new Date(
          val
        ).toISOString();
      },
    },
    endDateTime: {
      get() {
        return this.meetingObject.scheduledEndTimeInUTC
          ? new Date(this.meetingObject.scheduledEndTimeInUTC)
          : new Date();
      },
      set(val) {
        this.meetingObject.scheduledEndTimeInUTC = new Date(val).toISOString();
      },
    },
    waterMarkText() {
      return this.$t("agenda.lblMeetingDate");
    },
    commentModel() {
      return {
        type: "Expand",
        items: [
          "Bold",
          "Italic",
          "Underline",
          "StrikeThrough",
          "FontName",
          "FontSize",
          "FontColor",
          "BackgroundColor",
          "LowerCase",
          "UpperCase",
          "SuperScript",
          "SubScript",
          "|",
          "Formats",
          "Alignments",
          "NumberFormatList",
          "BulletFormatList",
          "Outdent",
          "Indent",
          "|",
          "CreateTable",
          "CreateLink",
          "|",
          "FullScreen",
          "|",
          "Undo",
          "Redo",
        ],
      };
    },
    selectedLang() {
      return localStorage.getItem("Lang#" + this.selectedTenant);
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    meetingTypesList() {
      return this.meetingTypes.filter((item) =>
        this.$security.checkMeetingTypeAccess(item.id, "CreateMeeting")
      );
    },
    isMeetingTypeDisabled() {
      return (
        Boolean(this.meetingObject.recurrenceId) ||
        this.meetingObject.topics.length > 0
      );
    },
    isMeetingRecurrence() {
      if (this.meetingObject.recurrenceData) {
        return this.meetingObject.recurrenceData.isMeetingRecurrence;
      }
      return false;
    },
    isSeriesDetailsTitle() {
      if (
        !this.isMeetingRecurrence &&
        !this.meetingObject.id &&
        this.isSeriesEdit
      ) {
        return false;
      }
      if (
        this.isMeetingRecurrence &&
        !this.meetingObject.id &&
        this.isSeriesEdit
      ) {
        return true;
      }
      if (!this.isSeriesEdit) {
        return false;
      }
      return true;
    },
    isDateRangePickerDisabled() {
      if (this.isMeetingRecurrence && this.isSeriesEdit) {
        return true;
      }
      return false;
    },
    updateDescription() {
      return this.meetingObject.description;
    },
    lblCopy() {
      return this.$t("poll.tipCopy");
    },
  },
  methods: {
    ...mapActions({
      updateMeetingData: "meeting/updateAgendaMeeting",
      createMeetingData: "meeting/createAgendaMeeting",
      getMeetingListApi: "meeting/getMeetingList",
      createMeetingSeries: "meeting/createMeetingSeries",
      updateMeetingSeries: "meeting/updateMeetingSeries",
      getDeletedUsersList: "meeting/getDeletedUsersListTemp",
      createDocuments: "document/createDocuments",
      deleteDocuments: "document/deleteDocuments",
    }),
    ...mapMutations({
      setScheduleMeetingError: "setScheduleMeetingError",
    }),
    descriptionChanges(val) {
      this.isDescriptionWithImage = val;
    },
    focusSelect(e) {
      this.scrolling(e.srcElement);
    },
    scrolling(element) {
      let dialogCardEl = document.getElementById("dialog-card-text");
      if (dialogCardEl) {
        dialogCardEl.addEventListener("scroll", (event) => {
          this.isTooltipDisabled = true;
          if (element && element.id == "meetingType") {
            if (this.$refs.refMeetingType.$refs.input) {
              this.$refs.refMeetingType.blur();
            }
          } else if (element && element.id == "resType") {
            if (this.$refs.refResType.$refs.input) {
              this.$refs.refResType.blur();
            }
          }
        });
        dialogCardEl.addEventListener("scrollend", (event) => {
          this.isTooltipDisabled = false;
        });
      }
    },

    showMessageSheet(message, messageType) {
      Object.assign(this, { displayMessage: true, message, messageType });
    },
    onParticipantChanged(attendees) {
      this.meetingObject.attendees = attendees;
    },
    async saveMeeting() {
      this.isPrevent = false;
      if (this.$refs.refMeetingForm.validate()) {
        try {
          this.isDataSaving = true;
          const meetingFormData = this.prepareMeetingFormData();

          let dataToSave = meetingFormData;


          if (meetingFormData.id && !this.isCopy) {
            this.isNewMeetingCreated = false;
            await this.updateExistingMeeting(dataToSave);
          } else {
            this.isNewMeetingCreated = true;

            if (this.isCopy) {
              dataToSave = normalizeMeetingObject(meetingFormData);
            }

            await this.createNewMeeting(dataToSave);
          }
          this.isDataSaving = false;
        } catch (error) {
          this.handleSaveError();
        }
      }
    },
    prepareMeetingFormData() {
      const meetingFormData = {
        ...this.meetingObject,
        attendees: this.meetingObject.attendees.map((item) => {
          const originalAttendee = this.originalMeetingData.attendees?.find(
            (att) => att.id === item.id && att.email === item.email
          )
          let itemObj = {
              id: item.id,
              email: item.email,
              emailList: item.emailList ? item.emailList : [],
              attendeNote: item?.attendeNote || originalAttendee?.attendeNote || "",
            };
          if (originalAttendee && originalAttendee.isGuest && (originalAttendee.firstName && originalAttendee.lastName)) {
              itemObj.firstName = originalAttendee.firstName;
              itemObj.lastName = originalAttendee.lastName;
              itemObj.title = originalAttendee.title;
              itemObj.isGuest = originalAttendee.isGuest;
            }
          return itemObj
        }),
        recurrenceData: this.recurrenceData,
        meetingTypeId: this.meetingObject.meetingType.id || "",
        meetingType: this.meetingObject.meetingType.label || "",
      };

      if (meetingFormData.meetingName.length > 100) {
        meetingFormData.meetingName =
          meetingFormData.meetingName.slice(0, 100) + "...";
      }

      return meetingFormData;
    },
    async updateExistingMeeting(meetingFormData) {
      let responseMeetingObject = null;
      if (!meetingFormData.description_meta) {
        meetingFormData.description_meta = [];
      }
      const { htmlContent, description_meta } =
        await this.uploadMeetingDescriptionImages(meetingFormData.description);
      meetingFormData.description = htmlContent;
      let update_description_meta = description_meta.map(
        ({ encoded_image, ...rest }) => rest
      );
      meetingFormData.description_meta = [...update_description_meta];

      const { recurrenceData, ...updateData } = meetingFormData;
      const responseData =
        meetingFormData.recurrenceId && this.isSeriesEdit
          ? await this.updateMeetingSeries(meetingFormData)
          : await this.updateMeetingData(updateData);

      if (
        meetingFormData.recurrenceId &&
        this.isSeriesEdit &&
        responseData.status
      ) {
        if (!_.isEmpty(responseData.data)) {
          responseMeetingObject = responseData.data;
        }
      } else {
        responseMeetingObject = responseData;
      }

      if (responseMeetingObject) {
        const attendees = await this.processAttendees(
          responseMeetingObject.attendees
        );

        this.emitMeetingAlert(
          "agenda.MeetingUpdated",
          responseMeetingObject.meetingName
        );
        this.handleMailSendConfirmation(
          responseMeetingObject,
          "UpdateMeeting",
          attendees
        );
      } else {
        this.emitMeetingAlert(
          "agenda.MeetingUpdated",
          meetingFormData.meetingName
        );
        this.$emit("input", false);
        this.$root.$emit("loadMeetings");
      }
    },
    async createNewMeeting(meetingFormData) {
      meetingFormData = this.initializeNewMeetingData(meetingFormData);

      if (!meetingFormData.description_meta) {
        meetingFormData.description_meta = [];
      }
      const { htmlContent, description_meta } =
        await this.uploadMeetingDescriptionImages(meetingFormData.description);
      meetingFormData.description = htmlContent;

      let update_description_meta = description_meta.map(
        ({ encoded_image, ...rest }) => rest
      );
      meetingFormData.description_meta = [...update_description_meta];

      const { recurrenceData, ...createData } = meetingFormData;
      const responseData = meetingFormData.recurrenceId
        ? await this.createMeetingSeries(meetingFormData)
        : await this.createMeetingData(createData);
      if (responseData.status) {
        const responseMeetingObject = responseData.data;
        const attendees = await this.processAttendees(
          responseMeetingObject.attendees
        );
        this.emitMeetingAlert(
          "agenda.MeetingCreated",
          responseMeetingObject.meetingName
        );
        this.handleMailSendConfirmation(
          responseMeetingObject,
          "SaveMeeting",
          attendees
        );
      }
    },
    initializeNewMeetingData(meetingFormData) {
      meetingFormData.id = "meeting" + createUUID();
      if (this.isCopy) {
        meetingFormData.recurrenceId = null;
      } else {
        const isRecurring = meetingFormData.recurrenceData?.isMeetingRecurrence;
        meetingFormData.recurrenceId = isRecurring ? createUUID() : null;
      }

      meetingFormData.tenantid = this.selectedTenant;
      meetingFormData.startMeetingTime = null;
      meetingFormData.endMeetingTime = null;
      meetingFormData.timerValue = 0;
      meetingFormData.elapsed = 0;
      meetingFormData.timeCounter = 0;
      meetingFormData.status = null;
      meetingFormData.topics = [];

      return meetingFormData;
    },
    async processAttendees(attendees) {
      let localUserList = [...this.userList];
      const userWithIdExists = (id) =>
        localUserList.some((obj) => obj.id === id);
      const unmatchedObjects = attendees.filter(
        (obj) => !userWithIdExists(obj.id)
      );
      const unmatchedIds = unmatchedObjects.map((obj) => obj.id);

      if (unmatchedIds.length) {
        const result = await this.getDeletedUsersList(unmatchedIds);
        if (result.length) {
          localUserList = localUserList.concat(result);
        }
      }
      return attendees.map((item) => {
        const uData = localUserList.find((u) => u.id == item.id);
        const contactEmailIds =
          uData && uData.contactEmailIds
            ? uData.contactEmailIds.filter((email) => email !== item.email)
            : [];
        return {
          ...item,
          title: item.title ? item.title : (uData.title || ""),
          firstName: item.firstName ? item.firstName : (uData.firstName || ""),
          lastName: item.lastName ? item.lastName : (uData.lastName || ""),
          profilePhoto: uData ? uData.profilePhoto : "",
          contactEmailIds: contactEmailIds,
        };
      });
    },
    emitMeetingAlert(messageKey, meetingName) {
      const params = {
        message: this.$t(messageKey, { meetingName }),
        messageType: "success",
      };
      this.$root.$emit("meetingAlert", params);
    },
    handleMailSendConfirmation(response, actionType, attendees) {
      if (attendees.length > 0) {
        response.attendees = attendees;
        this.showEmailDialog = true;
        this.emailMeetingObject = response;
        // this.mailSendConfirmation(
        //   response,
        //   actionType,
        //   this.$t("agenda.MeetingUpdated", {
        //     meetingName: response.meetingName,
        //   })
        // );
      } else {
        this.$emit("input", false);
        this.$root.$emit("loadMeetings");
      }
    },
    handleSaveError() {
      this.isDataSaving = false;
    },
    async mailSendConfirmation(meetingDetails, type, notificationMessage) {
      this.emailData = {
        open: true,
        type,
        meetingDetails,
        newDocuments: [],
        isPersonalised: true,
        hideOverlay: true,
      };
      this.mailNotificationMessage = notificationMessage;
    },
    onEmailDialogCancel() {
      this.emailData = {};
      this.$emit("input", false);
      this.$root.$emit("loadMeetings");
    },
    onEmailSend(isSuccess) {
      const message = isSuccess
        ? "SendEmailNotification"
        : "FaildEmailNotification";
      const messageType = isSuccess ? "success" : "error";

      const params = {
        message: this.$t(`agenda.${message}`),
        messageType,
      };
      this.$root.$emit("meetingAlert", params);
      this.$emit("input", false);
      this.$root.$emit("loadMeetings");
    },
    close() {
      if (this.isDirtyData && this.isDescriptionWithImage) {
        this.isPrevent = false;
        this.$emit("input", false);
      } else if (this.isDirtyData) {
        this.isPrevent = true;
      } else {
        this.$emit("input", false);
      }
    },
    createUUID,
    isURL,
    preventClose(status) {
      this.isPrevent = false;
      if (status) {
        this.$emit("input", false);
        // this.isDirty = false;
      }
    },
    onResetRecurrence,
    onMeetingNameChanged,
    onDiscardMeeting,
    AddMinutesToDate,
    onUpdateStartEndTime,
    uploadMeetingDescriptionImages,
    bindMeetingDecriptionImages,
    uploadImage,
    processImageTag,
    updateImageTag,
    deleteImageDocuments,
    checkDescriptionWithImage,
  },
};

async function checkDescriptionWithImage(description) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(description, "text/html");
  const imgTags = Array.from(doc.querySelectorAll("img")); // Collect all <img> tags

  // Initialize to false by default
  // this.isDescriptionWithImage = false;
  let isInvalidFileFound = false;
  // Process each <img> tag
  for (const imgTag of imgTags) {
    const imgSrc = imgTag.getAttribute("src");

    // Skip if src is invalid or starts with "IMAGE_" or "data:"
    if (!imgSrc || imgSrc.startsWith("IMAGE_") || imgSrc.startsWith("data:")) {
      continue;
    }

    try {
      const response = await fetch(imgSrc);
      if (!response.ok) throw new Error(`Failed to fetch ${imgSrc}`);

      const blob = await response.blob();
      const isImage = blob.type.startsWith("image/");

      // Mark true if blob is not an image
      if (!isImage) {
        this.isDescriptionWithImage = true;
        isInvalidFileFound = true;
        break; // Exit loop as soon as a non-image is found
      }
    } catch (error) {
      console.error(`Error fetching image: ${imgSrc}`, error);
    }
  }

  if (!isInvalidFileFound) {
    this.isDescriptionWithImage = false;
  }

  // If no img tags or no non-image files were found, keep as false
  this.isDescriptionWithImage =
    imgTags.length > 0 && this.isDescriptionWithImage;
}

function bindMeetingDecriptionImages() {
  let htmlContent = this.selectedMeeting.description;
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  var imgElements = doc.querySelectorAll("img");
  // Extract the src values
  var imgTags = [];
  imgElements.forEach(function (img) {
    imgTags.push(img);
  });
  for (const imgTag of imgTags) {
    let imgSrc = imgTag.getAttribute("src");
    if (imgSrc && imgSrc.startsWith("IMAGE_")) {
      const guid = imgSrc.split("IMAGE_")[1];
      const imgData = this.selectedMeeting.description_meta.find(
        (i) => i.documentId === guid
      );
      if (imgTag && imgData) {
        imgTag.src = `${imgData.encoded_image}`;
        imgTag.setAttribute("id", imgData.documentId);
      }
    }
  }

  htmlContent = doc.body.innerHTML;
  return htmlContent;
}

async function uploadMeetingDescriptionImages(htmlContent) {
  try {
    // let htmlContent = this.selectedMeeting.description;
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const imgTags = Array.from(doc.querySelectorAll("img")); // Collect img tags as an array
    if (!this.selectedMeeting.description_meta) {
      this.selectedMeeting.description_meta = [];
    }
    let description_meta = [...this.selectedMeeting.description_meta];
    let existingDocIds = [];

    // Process all img tags
    for (const imgTag of imgTags) {
      await this.processImageTag(imgTag, description_meta, existingDocIds);
    }

    htmlContent = doc.body.innerHTML; // Update htmlContent with the modified src values

    const deletedIds = getDeletedIds(description_meta, existingDocIds);
    const restArray = filterAndRemoveEncodedImage(
      description_meta,
      existingDocIds
    );

    // Handle deletion of removed image documents
    if (deletedIds.length) {
      if (!this.isMeetingRecurrence) {
        await this.deleteImageDocuments(deletedIds);
      }
      this.existingDocIds = [];
    }

    // Update description_meta and description
    // this.selectedMeeting.description_meta = restArray;
    // this.selectedMeeting.description = htmlContent;

    return { htmlContent, description_meta: restArray };
  } catch (error) {
    console.error("Error fetching the Blob:", error);
  }
}

// Process each image tag to handle file upload or ID assignment
async function processImageTag(imgTag, description_meta, existingDocIds) {
  const imgSrc = imgTag.getAttribute("src");

  // Process external image URLs (not starting with IMAGE_ or data:)
  if (imgSrc && !imgSrc.startsWith("IMAGE_") && !imgSrc.startsWith("data:")) {
    const documentId = await this.uploadImage(imgSrc);
    this.updateImageTag(imgTag, documentId, description_meta, existingDocIds);
  }
  // Handle base64 images
  else if (imgSrc.startsWith("data:") && imgTag.hasAttribute("id")) {
    const documentId = imgTag.id;
    imgTag.src = `IMAGE_${documentId}`;
    existingDocIds.push(documentId);
  }
}

// Upload an image, create a File object, and return the document ID
async function uploadImage(imgSrc) {
  const response = await fetch(imgSrc);
  const blob = await response.blob();
  const file = new File([blob], "filename.png", { type: blob.type });

  const documentId = createUUID();
  const formData = new FormData();

  formData.append("tenantId", this.selectedTenant);
  formData.append("topicId", "");
  formData.append("meetingId", this.meetingObject.id);
  formData.append("agendaId", "");
  formData.append("directoryId", "");
  formData.append("peopleId", "");
  formData.append("file", file);
  formData.append("documentId", documentId);
  formData.append("chunk_upload", false);
  formData.append("filename", file.name);
  formData.append("content_type", file.type);
  formData.append("document_type", "description_image");

  await this.createDocuments(formData); // Upload the image
  return documentId;
}

// Update the image tag's src attribute and track document metadata
function updateImageTag(imgTag, documentId, description_meta, existingDocIds) {
  imgTag.src = `IMAGE_${documentId}`;
  description_meta.push({ documentId });
  existingDocIds.push(documentId);
}

// Get the IDs of images that were removed from the updated document list
function getDeletedIds(description_meta, existingDocIds) {
  return description_meta
    .filter((item) => !existingDocIds.includes(item.documentId))
    .map((item) => item.documentId);
}

// Filter the description_meta array and remove the encoded_image key
function filterAndRemoveEncodedImage(description_meta, existingDocIds) {
  return description_meta
    .filter((item) => existingDocIds.includes(item.documentId))
    .map(({ encoded_image, ...rest }) => rest);
}

// Handle deletion of documents using their IDs
async function deleteImageDocuments(deletedIds) {
  const promises = deletedIds.map((id) => this.deleteDocuments(id));
  await Promise.all(promises);
}

function onUpdateStartEndTime(timeData) {
  const startTimeDate = new Date(timeData.startTimeDate);
  const endTimeDate = new Date(timeData.endTimeDate);
  if (new Date(this.meetingObject.scheduletStartTimeInUTC) !== startTimeDate) {
    this.meetingObject.scheduletStartTimeInUTC = startTimeDate.toISOString();
  }
  if (new Date(this.meetingObject.scheduledEndTimeInUTC) !== endTimeDate) {
    this.meetingObject.scheduledEndTimeInUTC = endTimeDate.toISOString();
  }
}

function AddMinutesToDate(date, minutes) {
  return new Date(date.getTime() + minutes * 60000);
}

function onDiscardMeeting() {
  this.$emit("input", false);
}

function onMeetingNameChanged(value) {
  if (this.isSeriesEdit) {
    this.recurrenceData.recurrence_title = value;
  }
}

function onResetRecurrence() {
  if (!this.meetingObject.id) {
    this.meetingObject.recurrenceData = _.cloneDeep(
      this.originalMeetingData.recurrenceData
    );
    this.meetingObject.scheduletStartTimeInUTC = _.cloneDeep(
      this.originalMeetingData.scheduletStartTimeInUTC
    );
    this.meetingObject.scheduledEndTimeInUTC = _.cloneDeep(
      this.originalMeetingData.scheduledEndTimeInUTC
    );
  }
}
function gettersMap() {
  return mapGetters({
    meetingTypes: "tenant/agendaMeetingTypes",
    resolutionTypesList: "meeting/getResolutionTypesList",
  });
}

function normalizeMeetingObject(input = {}) {
  const defaultMeeting = {
    actualEndTimeInUTC: null,
    actualStartTimeInUTC: null,
    attendees: [],
    description: "",
    description_meta: [],
    documents: null,
    elapsed: 0,
    endMeetingTime: null,
    has_past_meetings: false,
    id: null,
    location: null,
    meeting_link: null,
    meetingName: null,
    meetingTheme: null,
    meetingType: null,
    meetingTypeId: null,
    recurrenceId: null,
    recurrenceData: {
      isMeetingRecurrence: false,
      recurrence_title: "",
      recurrenceStartDate: new Date().toISOString(),
      recurrenceEndDate: null,
      recurrencePattern: "month",
      repeatInterval: 1,
      selectedDays: [],
      recurrenceOnDay: {
        selectedValue: 0,
        day: new Date().getDate(),
        selectedWeekSlot: "first",
        selectedWeekDay: "sunday",
        selectedMonth: new Date().getMonth(),
      },
    },
    resolutionType: null,
    scheduledEndTimeInUTC: null,
    scheduletStartTimeInUTC: null,
    startMeetingTime: null,
    status: null,
    tenantid: null,
    timeCounter: 0,
    timerValue: 0,
    topics: [],
  };

  const normalized = {};
  for (const key in defaultMeeting) {
    normalized[key] = key in input ? input[key] : defaultMeeting[key];
  }

  return normalized;
}
</script>

<style scoped>
.textField {
  font-family: "Noto Sans JP" !important;
  font-style: normal !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 18px !important;
  min-height: 48px !important;
  max-height: 68px !important;
  color: var(--v-neutralGray-base) !important;
  align-items: center !important;
}
.textField > .v-input__control > .v-input__slot {
  color: var(--v-neutralGray-base) !important;
  height: 48px !important;
}
.textField /deep/ .v-input__slot {
  min-height: 48px !important;
}
.locationField /deep/ .v-input__slot {
  min-height: 54px !important;
}
.textField /deep/ textarea {
  margin-top: 22px !important;
}
.textField /deep/ .v-label {
  line-height: 16px !important;
}
.selectField /deep/ .v-input__slot {
  min-height: 48px !important;
}
.selectField /deep/ .v-label {
  line-height: 16px !important;
}
.selectField {
  font-family: "Noto Sans JP" !important;
  font-style: normal !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 18px !important;
}
.v-text-field--filled {
  border-radius: 0px !important;
}
::v-deep .v-banner__wrapper {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  padding-left: 10px !important;
  padding-top: 2px !important;
  padding-bottom: 5px !important;
}

::v-deep .attendeeClass {
  max-height: 100%;
}
::v-deep .e-datetime-wrapper {
  /* border: 1px solid gray !important;
  border-radius: 4px !important; */
  padding-left: 5px !important;
  padding-right: 5px !important;
  height: 38px;
  font-size: 15px !important;
  /* background-color: rgba(0, 0, 0, 0.06) !important; */
}

::v-deep .e-datetime-wrapper .e-input-group-icon {
  min-height: 18px !important;
  min-width: 18px !important;
}

.e-code-mirror::before {
  content: "\e345";
}

.e-html-preview::before {
  content: "\e350";
}

.CodeMirror-linenumber,
.CodeMirror-gutters {
  display: none;
}
.sb-header {
  z-index: 100;
}

.sb-content.e-view.hide-header {
  top: 0 !important;
}

.sb-header.e-view.hide-header {
  display: none;
}

.fabric-dark .cm-s-default .cm-tag,
.bootstrap5-dark .cm-s-default .cm-tag,
.material-dark .cm-s-default .cm-tag,
.tailwind-dark .cm-s-default .cm-tag,
.highcontrast .cm-s-default .cm-tag {
  color: #00ff00;
}

.fabric-dark .cm-s-default .cm-string,
.bootstrap5-dark .cm-s-default .cm-string,
.material-dark .cm-s-default .cm-string,
.tailwind-dark .cm-s-default .cm-string {
  color: blue;
}

.highcontrast .cm-s-default .cm-string {
  color: #ffd939;
}

.fabric-dark .cm-s-default .cm-attribute,
.bootstrap5-dark .cm-s-default .cm-attribute,
.material-dark .cm-s-default .cm-attribute,
.tailwind-dark .cm-s-default .cm-attribute,
.highcontrast .cm-s-default .cm-attribute {
  color: #f00;
}

.fabric-dark .CodeMirror,
.bootstrap5-dark .CodeMirror,
.material-dark .CodeMirror,
.tailwind-dark .CodeMirror {
  background: #303030;
  color: white;
}

.highcontrast .CodeMirror {
  background: black;
  color: white;
}

.e-richtexteditor {
  color: #333;
  display: block;
  position: relative;
  z-index: 0;
}
.e-richtexteditor .e-rte-content .e-content pre {
  padding: 10px;
  background: #f4f5f7;
}

.fabric-dark .e-richtexteditor .e-rte-content .e-content pre,
.bootstrap5-dark .e-richtexteditor .e-rte-content .e-content pre,
.material-dark .e-richtexteditor .e-rte-content .e-content pre,
.tailwind-dark .e-richtexteditor .e-rte-content .e-content pre,
.highcontrast .e-richtexteditor .e-rte-content .e-content pre {
  padding: 10px;
  background: #303030;
}
::v-deep .url-input-error {
  color: red;
}
</style>